import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth, API } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Trophy, CreditCard, Shield, Tag, DollarSign, TrendingUp, Building } from "lucide-react";
import AdminPrizes from "../components/AdminPrizes";
import AdminSiteEditor from "../components/AdminSiteEditor";
import AdminPayments from "../components/AdminPayments";

const TABS = ["PANORAMICA", "SITO", "PIANI", "LICENZE", "PAGAMENTI", "COUPON", "ORGANIZZAZIONI", "PREMI"];
const TAB_KEY = { PANORAMICA: "OVERVIEW", SITO: "SITE", PIANI: "PLANS", LICENZE: "LICENSES", PAGAMENTI: "PAYMENTS", COUPON: "COUPONS", ORGANIZZAZIONI: "ORGANIZATIONS", PREMI: "PRIZES" };

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("OVERVIEW");
  const [freeEvent, setFreeEvent] = useState(false);
  useEffect(() => { axios.get(`${API}/settings/public`).then(s => setFreeEvent(!!s.data.free_event_license)).catch(() => {}); }, []);
  const toggleFreeEvent = async () => {
    const { data } = await axios.put(`${API}/admin/settings/free-event`, { enabled: !freeEvent });
    setFreeEvent(data.free_event_license);
    toast.success(data.free_event_license ? "Licenza Evento ora GRATIS" : "Licenza Evento a pagamento");
  };
  const [data, setData] = useState({ overview: null, plans: [], licenses: [], payments: [], coupons: [], organizations: [] });

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "SUPER_ADMIN") { nav("/login"); return; }
    loadAll();
  }, [loading, user]);

  const loadAll = async () => {
    const [o, p, l, py, c, org] = await Promise.all([
      axios.get(`${API}/admin/business/overview`).catch(() => ({data: null})),
      axios.get(`${API}/admin/plans`).catch(() => ({data: []})),
      axios.get(`${API}/admin/licenses`).catch(() => ({data: []})),
      axios.get(`${API}/admin/payments`).catch(() => ({data: []})),
      axios.get(`${API}/admin/coupons`).catch(() => ({data: []})),
      axios.get(`${API}/admin/organizations`).catch(() => ({data: []})),
    ]);
    setData({ overview: o.data, plans: p.data, licenses: l.data, payments: py.data, coupons: c.data, organizations: org.data });
  };

  const publishPlans = async (beta) => { try { await axios.post(`${API}/admin/plans/publish`, { confirm: "PUBBLICA", ...(beta === undefined ? {} : { beta_label: beta }) }); toast.success("Prezzi pubblicati: la pagina Rally Love PRO è aggiornata"); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const updatePlan = async (code, patch) => {
    const publish = "active" in patch ? false : window.confirm(`Applicare subito la modifica al piano ${code} sulla pagina Rally Love PRO?\n\nOK = pubblica ora · Annulla = salva come bozza (anteprima)`);
    try {
      await axios.put(`${API}/admin/plans/${code}`, { ...patch, publish });
      toast.success("active" in patch ? `Piano ${code} aggiornato` : publish ? `Piano ${code} pubblicato: la pagina Rally Love PRO è aggiornata` : `Bozza salvata per ${code}: premi PUBBLICA ORA quando vuoi renderla pubblica`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    loadAll();
  };

  const [couponForm, setCouponForm] = useState({ code: "", discount_type: "percentage", discount_amount: 10, max_uses: 100, active: true });
  const createCoupon = async () => {
    try {
      await axios.post(`${API}/admin/coupons`, couponForm);
      toast.success("Coupon creato");
      setCouponForm({ code: "", discount_type: "percentage", discount_amount: 10, max_uses: 100, active: true });
      loadAll();
    } catch { toast.error("Errore"); }
  };

  const [manualLic, setManualLic] = useState({ organizer_id: "", plan_code: "EVENT", duration_days: 365, events_allowed: 1 });
  const grantManual = async () => {
    try {
      await axios.post(`${API}/admin/licenses/manual`, manualLic);
      toast.success("Licenza assegnata");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const suspendLicense = async (lid, status) => {
    await axios.put(`${API}/admin/licenses/${lid}`, { status });
    toast.success(`Licenza → ${status}`);
    loadAll();
  };

  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="admin" />
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="pill-badge mb-2"><Shield className="w-3 h-3" /> SUPER ADMIN</div>
              <h1 className="font-display font-black text-3xl md:text-4xl" data-testid="admin-title">CENTRO DI CONTROLLO BUSINESS</h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(TAB_KEY[t])}
                data-testid={`admin-tab-${TAB_KEY[t].toLowerCase()}`}
                className={`px-4 py-2 rounded-full font-mono-rl text-xs uppercase tracking-widest transition ${tab === TAB_KEY[t] ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-300 hover:border-[#FF1E27]/40"}`}>
                {t}
              </button>
            ))}
          </div>

          {tab === "OVERVIEW" && data.overview && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Utenti totali" value={data.overview.total_users} color="text-[#00F0FF]" />
                <StatCard icon={Building} label="Organizzatori" value={data.overview.total_organizers} color="text-[#FF9F0A]" />
                <StatCard icon={Trophy} label="Organizzatori PRO" value={data.overview.pro_organizers} color="text-[#30D158]" />
                <StatCard icon={Shield} label="Licenze attive" value={data.overview.active_licenses} color="text-[#FF1E27]" />
                <StatCard icon={DollarSign} label="Ricavi" value={`€${data.overview.revenue.toFixed(2)}`} color="text-[#30D158]" />
                <StatCard icon={TrendingUp} label="Media / Organizzatore" value={`€${data.overview.avg_revenue_per_organizer.toFixed(0)}`} color="text-[#FF9F0A]" />
                <StatCard icon={CreditCard} label="Licenze vendute" value={data.overview.licenses_sold} color="text-[#FF1E27]" />
                <StatCard icon={Shield} label="Scadute" value={data.overview.expired_licenses} color="text-slate-400" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SplitCard label="Licenze Evento" value={data.overview.event_licenses_sold} />
                <SplitCard label="Licenze Season" value={data.overview.season_licenses} />
                <SplitCard label="Licenze Unlimited" value={data.overview.unlimited_licenses} />
              </div>
            </div>
          )}

          {tab === "PLANS" && (
            <div className="rl-card p-6 space-y-4">
              <div className="flex items-center justify-between border rounded-lg p-4" style={{ borderColor: freeEvent ? "rgba(48,209,88,0.4)" : "rgba(255,255,255,0.1)" }} data-testid="free-event-switch-card">
                <div>
                  <div className="font-semibold">Licenza Evento GRATIS</div>
                  <div className="text-xs text-slate-400">Quando è ON gli organizer attivano la Event License senza pagare. Stato: <b className={freeEvent ? "text-[#30D158]" : "text-slate-300"}>{freeEvent ? "ON" : "OFF"}</b></div>
                </div>
                <button onClick={toggleFreeEvent} data-testid="free-event-toggle"
                  className={`px-4 py-2 rounded-full font-mono-rl text-xs tracking-widest ${freeEvent ? "bg-[#30D158] text-black" : "border border-white/20 text-slate-300"}`}>
                  {freeEvent ? "GRATIS ON" : "GRATIS OFF"}</button>
              </div>
              <h2 className="font-display font-bold text-xl">Plans & Pricing</h2>
              <div className="text-xs text-slate-400">Le modifiche a prezzi, promozioni e visibilità vengono salvate come <b>bozza</b>: nulla è pubblicato finché non confermi dall'anteprima.</div>
              <div className="flex flex-wrap gap-2"><button className="btn-crimson !py-2 !px-4 text-xs min-h-[44px]" data-testid="plans-preview-btn" onClick={async () => { const { data } = await axios.get(`${API}/admin/plans/preview`); const ch = data.filter(x => x.changes.length); const txt = ch.length ? ch.map(x => `${x.code}: ${x.changes.map(k => `${k} ${JSON.stringify(x.live[k])} → ${JSON.stringify(x.preview[k])}`).join(", ")}`).join("\n") : "Nessuna modifica in bozza."; const c = window.prompt(`ANTEPRIMA MODIFICHE\n${txt}\n\nScrivi PUBBLICA per applicare ai prezzi pubblici.`); if (c === "PUBBLICA") publishPlans(); else if (c) toast.error("Scrivi PUBBLICA per confermare"); }}>ANTEPRIMA & PUBBLICA</button>
                <button className="btn-outline-rl !py-2 !px-4 text-xs min-h-[44px]" data-testid="plans-beta-toggle" onClick={async () => { const on = window.confirm("Mostrare l'etichetta 'PREZZI BETA RISERVATI AI PRIMI ORGANIZZATORI' nella pagina Licenze? OK = sì, Annulla = no"); publishPlans(on); }}>ETICHETTA PREZZI BETA</button></div>
              {data.plans.map(p => (
                <div key={p.code} className="border border-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div><span className="pill-badge mr-3">{p.code}</span><span className="font-semibold">{p.name}</span></div>
                    <span className={`text-xs font-mono-rl ${p.active ? "text-[#30D158]" : "text-slate-500"}`}>{p.active ? "ATTIVO" : "NON ATTIVO"}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <Editable label="Nome" value={p.draft?.name ?? p.name} onSave={v => updatePlan(p.code, { name: v })} testid={`plan-${p.code}-name`} />
                    <div className="col-span-2 md:col-span-4"><Editable label="Descrizione" value={p.draft?.description ?? p.description} onSave={v => updatePlan(p.code, { description: v })} testid={`plan-${p.code}-description`} /></div>
                    <div className="col-span-2 md:col-span-5">
                      <div className="text-[10px] font-mono-rl text-slate-500 tracking-widest mb-1">FUNZIONALITÀ (una per riga)</div>
                      <textarea key={JSON.stringify(p.draft?.features || p.features)} defaultValue={(p.draft?.features || p.features || []).join("\n")} onBlur={e => { const f = e.target.value.split("\n").map(x => x.trim()).filter(Boolean); if (f.join("|") !== (p.features || []).join("|")) updatePlan(p.code, { features: f }); }}
                        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs min-h-[90px] outline-none focus:border-[#FF1E27]" data-testid={`plan-${p.code}-features`} />
                    </div>
                    <Editable label="Prezzo €" value={p.draft?.price_eur ?? p.price_eur} onSave={v => updatePlan(p.code, { price_eur: parseFloat(v) })} testid={`plan-${p.code}-price`} />
                    <Editable label="Durata (giorni)" value={p.draft?.duration_days ?? p.duration_days} onSave={v => updatePlan(p.code, { duration_days: parseInt(v) })} testid={`plan-${p.code}-duration`} />
                    <Editable label="Eventi inclusi" value={p.draft?.events_allowed ?? p.events_allowed} onSave={v => updatePlan(p.code, { events_allowed: parseInt(v) })} testid={`plan-${p.code}-events`} />
                    <Editable label="IVA %" value={p.draft?.vat_pct ?? p.vat_pct ?? 22} onSave={v => updatePlan(p.code, { vat_pct: parseInt(v) })} testid={`plan-${p.code}-vat`} />
                    <Editable label="Sponsor inclusi" value={p.draft?.sponsor_logos ?? p.sponsor_logos ?? 0} onSave={v => updatePlan(p.code, { sponsor_logos: parseInt(v) })} testid={`plan-${p.code}-sponsors`} />
                    <Editable label="Prezzo promo €" value={p.draft?.promo_price_eur ?? p.promo_price_eur ?? ""} onSave={v => updatePlan(p.code, { promo_price_eur: v === "" ? null : parseFloat(v) })} testid={`plan-${p.code}-promo`} />
                    <Editable label="Inizio promo (AAAA-MM-GG)" value={p.draft?.promo_start ?? p.promo_start ?? ""} onSave={v => updatePlan(p.code, { promo_start: v })} testid={`plan-${p.code}-promo-start`} />
                    <Editable label="Fine promo (AAAA-MM-GG)" value={p.draft?.promo_end ?? p.promo_end ?? ""} onSave={v => updatePlan(p.code, { promo_end: v })} testid={`plan-${p.code}-promo-end`} />
                    <Editable label="Ordine" value={p.draft?.order ?? p.order ?? p.tier} onSave={v => updatePlan(p.code, { order: parseInt(v) })} testid={`plan-${p.code}-order`} />
                    <Editable label="Badge" value={p.draft?.badge ?? p.badge ?? ""} onSave={v => updatePlan(p.code, { badge: v })} testid={`plan-${p.code}-badge`} />
                    <Editable label="CTA (ATTIVA/CONTATTACI/SU INVITO)" value={p.draft?.cta ?? p.cta ?? "ATTIVA"} onSave={v => updatePlan(p.code, { cta: v })} testid={`plan-${p.code}-cta`} />
                    <Editable label="Rinnovo (manual/none)" value={p.draft?.renewal ?? p.renewal ?? "manual"} onSave={v => updatePlan(p.code, { renewal: v })} testid={`plan-${p.code}-renewal`} />
                    <button onClick={() => updatePlan(p.code, { visible: !(p.draft?.visible ?? p.visible ?? true) })} className="btn-outline-rl !py-2 !px-3 text-xs" data-testid={`plan-${p.code}-visible`}>{(p.draft?.visible ?? p.visible ?? true) ? "VISIBILE" : "NASCOSTO"}</button>
                    <button onClick={() => updatePlan(p.code, { invite_only: !(p.draft?.invite_only ?? p.invite_only) })} className="btn-outline-rl !py-2 !px-3 text-xs" data-testid={`plan-${p.code}-invite`}>{(p.draft?.invite_only ?? p.invite_only) ? "SU INVITO" : "ACQUISTABILE"}</button>
                    {p.draft && Object.keys(p.draft).length > 0 && <div className="col-span-2 md:col-span-5 flex flex-wrap items-center gap-3 rl-card border-[#FF9F0A]/50 p-2 text-[11px]" data-testid={`plan-${p.code}-draft`}><span className="text-[#FF9F0A] font-mono-rl">BOZZA NON PUBBLICATA: {Object.keys(p.draft).map(k => `${k} → ${JSON.stringify(p.draft[k])}`).join(", ")}</span><button onClick={() => publishPlans()} className="btn-crimson !py-1.5 !px-3 text-[10px] min-h-[36px]" data-testid={`plan-${p.code}-publish-now`}>PUBBLICA ORA</button></div>}
                    <div>
                      <div className="text-xs font-mono-rl tracking-widest text-slate-500 mb-1">VALUTA</div>
                      <div className="text-slate-300 py-2">{p.currency}</div>
                    </div>
                    <button onClick={() => updatePlan(p.code, { active: !p.active })}
                      data-testid={`plan-${p.code}-toggle`}
                      className="btn-outline-rl !py-2 !px-3 text-xs">Attiva/Disattiva</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "LICENSES" && (
            <div className="space-y-6">
              <div className="rl-card p-6">
                <h2 className="font-display font-bold text-xl mb-4">Assegna licenza manuale</h2>
                <div className="grid md:grid-cols-5 gap-3">
                  <select value={manualLic.organizer_id} onChange={e => setManualLic({...manualLic, organizer_id: e.target.value})}
                    data-testid="manual-lic-org" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                    <option value="">Seleziona organizzatore</option>
                    {data.organizations.map(o => <option key={o.id} value={o.id}>{o.organizer_name}</option>)}
                  </select>
                  <select value={manualLic.plan_code} onChange={e => setManualLic({...manualLic, plan_code: e.target.value})}
                    data-testid="manual-lic-plan" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                    {data.plans.map(p => <option key={p.code}>{p.code}</option>)}
                  </select>
                  <input type="number" value={manualLic.duration_days} onChange={e => setManualLic({...manualLic, duration_days: parseInt(e.target.value)||365})}
                    data-testid="manual-lic-duration" placeholder="Giorni" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                  <input type="number" value={manualLic.events_allowed} onChange={e => setManualLic({...manualLic, events_allowed: parseInt(e.target.value)||1})}
                    data-testid="manual-lic-events" placeholder="Eventi" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                  <button onClick={grantManual} data-testid="grant-manual-btn" className="btn-crimson !py-2 text-sm">ASSEGNA</button>
                </div>
              </div>
              <div className="rl-card p-6 overflow-x-auto">
                <h2 className="font-display font-bold text-xl mb-4">Licenze</h2>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs font-mono-rl text-slate-500 uppercase tracking-widest text-left border-b border-white/5">
                    <th className="py-2">Piano</th><th>Org</th><th>Stato</th><th>Eventi</th><th>Scadenza</th><th></th>
                  </tr></thead>
                  <tbody>
                    {data.licenses.map(l => (
                      <tr key={l.id} className="border-b border-white/5">
                        <td className="py-3 pr-3"><span className="pill-badge">{l.plan_code}</span></td>
                        <td className="pr-3 font-mono-rl text-xs">{l.organizer_id.slice(0,8)}…</td>
                        <td className={`pr-3 text-xs font-mono-rl ${l.status === "ACTIVE" ? "text-[#30D158]" : "text-slate-400"}`}>{l.status}</td>
                        <td className="pr-3 font-mono-rl text-xs">{l.events_used}/{l.events_allowed === 0 ? "∞" : l.events_allowed}</td>
                        <td className="pr-3 font-mono-rl text-xs">{new Date(l.expiration_date).toLocaleDateString()}</td>
                        <td className="text-right">
                          {l.status === "ACTIVE" ? (
                            <button onClick={() => suspendLicense(l.id, "SUSPENDED")} data-testid={`suspend-${l.id}`}
                              className="text-xs text-[#FF1E27] hover:text-white">Sospendi</button>
                          ) : (
                            <button onClick={() => suspendLicense(l.id, "ACTIVE")} data-testid={`reactivate-${l.id}`}
                              className="text-xs text-[#30D158] hover:text-white">Riattiva</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "PAYMENTS" && <AdminPayments />}

          {tab === "COUPONS" && (
            <div className="space-y-6">
              <div className="rl-card p-6">
                <h2 className="font-display font-bold text-xl mb-4">Crea coupon</h2>
                <div className="grid md:grid-cols-5 gap-3">
                  <input value={couponForm.code} onChange={e => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                    data-testid="new-coupon-code" placeholder="CODICE" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono-rl" />
                  <select value={couponForm.discount_type} onChange={e => setCouponForm({...couponForm, discount_type: e.target.value})}
                    data-testid="new-coupon-type" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                    <option value="percentage">Percentuale</option><option value="fixed">Importo fisso €</option>
                  </select>
                  <input type="number" value={couponForm.discount_amount} onChange={e => setCouponForm({...couponForm, discount_amount: parseFloat(e.target.value)||0})}
                    data-testid="new-coupon-amount" placeholder="Importo" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                  <input type="number" value={couponForm.max_uses} onChange={e => setCouponForm({...couponForm, max_uses: parseInt(e.target.value)||0})}
                    data-testid="new-coupon-uses" placeholder="Utilizzi max" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                  <button onClick={createCoupon} disabled={!couponForm.code}
                    data-testid="create-coupon-btn" className="btn-crimson !py-2 text-sm">CREA</button>
                </div>
              </div>
              <div className="rl-card p-6">
                <h2 className="font-display font-bold text-xl mb-4">Coupon attivi</h2>
                <div className="space-y-2">
                  {data.coupons.map(c => (
                    <div key={c.code} className="flex items-center justify-between border-b border-white/5 py-3">
                      <div className="flex items-center gap-3">
                        <Tag className="w-4 h-4 text-[#FF9F0A]" />
                        <div>
                          <div className="font-mono-rl font-bold text-lg">{c.code}</div>
                          <div className="text-xs text-slate-500">{c.discount_amount}{c.discount_type === "percentage" ? "%" : "€"} · {c.current_uses}/{c.max_uses || "∞"} utilizzi</div>
                        </div>
                      </div>
                      <span className={`text-xs font-mono-rl ${c.active ? "text-[#30D158]" : "text-slate-500"}`}>{c.active ? "ATTIVO" : "NON ATTIVO"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "SITE" && <AdminSiteEditor />}
          {tab === "PRIZES" && <AdminPrizes />}
          {tab === "ORGANIZATIONS" && (
            <div className="rl-card p-6 overflow-x-auto">
              <h2 className="font-display font-bold text-xl mb-4">Organizzazioni</h2>
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-mono-rl text-slate-500 uppercase tracking-widest text-left border-b border-white/5">
                  <th className="py-2">Organizzatore</th><th>Paese</th><th>Contatto</th><th>Rally/Anno</th>
                </tr></thead>
                <tbody>
                  {data.organizations.map(o => (
                    <tr key={o.id} className="border-b border-white/5">
                      <td className="py-3 font-semibold">{o.organizer_name}</td>
                      <td>{o.country}</td>
                      <td className="text-xs text-slate-400">{o.contact_name} · {o.email}</td>
                      <td className="font-mono-rl">{o.rallies_per_year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rl-card p-5">
    <div className="flex items-center gap-2 text-xs font-mono-rl tracking-widest text-slate-400 uppercase mb-2">
      <Icon className={`w-4 h-4 ${color}`} /> {label}
    </div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);

const SplitCard = ({ label, value }) => (
  <div className="rl-card p-6 flex items-center justify-between">
    <div className="text-sm text-slate-300">{label}</div>
    <div className="font-mono-rl text-2xl font-bold text-[#FF9F0A]">{value}</div>
  </div>
);

const Editable = ({ label, value, onSave, testid }) => {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const dirty = String(v) !== String(value ?? "");
  return (
    <div>
      <div className="text-xs font-mono-rl tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex gap-1">
        <input value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && dirty) onSave(v); }} data-testid={`${testid}-input`}
          className={`w-full bg-black/40 border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF1E27] min-h-[40px] ${dirty ? "border-[#FF9F0A]" : "border-white/10"}`} />
        <button onClick={() => onSave(v)} disabled={!dirty} data-testid={`${testid}-save`} aria-label={`Salva ${label}`}
          className={`text-[10px] font-mono-rl px-2 min-h-[40px] rounded whitespace-nowrap ${dirty ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-500"}`}>SALVA</button>
      </div>
    </div>
  );
};
